import { useState } from 'react'
import { ArrowRight } from 'lucide-react'
import { useCatalogEntriesByIds, useChangeProductCode } from '@/hooks/useProducts'
import { divisionSuffixOf } from '@/lib/productSplit'
import type { CreatedProduct, ProductCatalogEntry } from '@/types/products'
import { ProductCatalogPicker } from '@/components/procedure/ProductCatalogPicker'
import {
  CHANGE_CODE_REASONS,
  ReasonField,
  composeReason,
  reasonIsComplete,
} from '@/components/procedure/ReasonField'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

/**
 * Change product code (z8nrz7e72t): re-code one bag.
 *
 * The server creates a NEW product under the chosen code with the same DIN and
 * the same division suffix, and keeps the old one as superseded history. The
 * code shown before confirming is worked out the same way the server does it —
 * the new base code plus this product's own suffix — never with a minted "A0",
 * which is what a first SPLIT produces (see `productSplit.ts`).
 *
 * The picker offers Active codes only, like Create Product.
 */
export function ChangeProductCodeDialog({
  product,
  onClose,
}: {
  /** null while closed. */
  product: CreatedProduct | null
  onClose: () => void
}) {
  return (
    <Dialog open={!!product} onOpenChange={next => !next && onClose()}>
      <DialogContent className='sm:max-w-5xl'>
        {/* Keyed so each opening starts with nothing picked. */}
        {product && <ChangeProductCodeForm key={product.id} product={product} onClose={onClose} />}
      </DialogContent>
    </Dialog>
  )
}

function ChangeProductCodeForm({ product, onClose }: { product: CreatedProduct; onClose: () => void }) {
  const change = useChangeProductCode()
  const current = useCatalogEntriesByIds([product.catalogEntryId])
  const [target, setTarget] = useState<ProductCatalogEntry | null>(null)
  const [reason, setReason] = useState('')
  const [detail, setDetail] = useState('')

  const baseCode = current.byId.get(product.catalogEntryId)?.productDescriptionCode
  const suffix = baseCode === undefined ? null : divisionSuffixOf(product.productDescriptionCode, baseCode)
  const newCode = target && suffix !== null ? `${target.productDescriptionCode}${suffix}` : null
  const sameCode = target?.id === product.catalogEntryId
  const canSubmit = !!target && !sameCode && reasonIsComplete(reason, detail) && !change.isPending

  return (
    <>
      <DialogHeader>
        <DialogTitle>Change product code</DialogTitle>
        <DialogDescription>
          DIN {product.din} keeps its DIN, volume, results and location under the new code. The
          current product is kept as superseded history and can no longer be split, pooled, shipped
          or labelled. Print a new label once the code has changed.
        </DialogDescription>
      </DialogHeader>

      <div className='flex flex-wrap items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 text-sm'>
        <span className='font-mono font-medium'>{product.productDescriptionCode}</span>
        <ArrowRight className='h-4 w-4 text-muted-foreground' />
        <span className='font-mono font-medium'>{newCode ?? '—'}</span>
        {suffix && (
          <span className='text-xs text-muted-foreground'>division {suffix} carried over</span>
        )}
      </div>

      <ProductCatalogPicker selected={target} onSelect={setTarget} />
      {sameCode && <p className='text-sm text-destructive'>That is the product's current code.</p>}

      <ReasonField
        presets={CHANGE_CODE_REASONS}
        reason={reason}
        detail={detail}
        onReasonChange={setReason}
        onDetailChange={setDetail}
      />

      {change.error instanceof Error && <p className='text-sm text-destructive'>{change.error.message}</p>}

      <DialogFooter>
        <Button variant='outline' onClick={onClose}>
          Cancel
        </Button>
        <Button
          disabled={!canSubmit}
          onClick={() =>
            target &&
            change.mutate(
              { productId: product.id, catalogEntryId: target.id, reason: composeReason(reason, detail) },
              { onSuccess: onClose },
            )
          }
        >
          {change.isPending ? 'Changing…' : 'Change code'}
        </Button>
      </DialogFooter>
    </>
  )
}
